import stats from "./content/site-stats.json";
import team from "./content/team.json";
import { CTABand, PrimaryButton } from "../components/Buttons";
import { useMeta } from "../hooks/useMeta";
import { Shell, H2, Reveal } from "./parts";

const HOW = [
  { title: "We've been where you are.", body: "We know what it's like to operate with limited resources in a crowded marketplace." },
  { title: "Strategy and support, not just execution.", body: "We don't just deliver premier ads and email services; we work closely with your team to give you the strategy and support that drives consistent, long-term growth." },
  { title: "A close, hands-on relationship.", body: "All we ask is that you commit to it so we can be as impactful as possible for your brand." },
];

export default function About() {
  useMeta({ title: "About | Smart Marketer Agency", description: "Built by operators. The same team that built $200 million in revenue for its own brands, working on your account." });
  return (
    <main data-testid="draft-about">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-20 md:py-28">
          <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Built by operators.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">Smart Marketer has been growing digital brands for over 15 years. We've built our own businesses from scratch that have generated $200 million in revenue, educated hundreds of thousands of marketers and guided the growth of over 10,000 businesses. The Agency is that same team, working on your account.</p>
        </div>
      </section>

      <Shell testId="leadership">
        <H2>Leadership.</H2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 md:grid-cols-4">
          {team.leadership.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06} className="rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-6">
              <img src={p.photo || stats.images.leaders} alt={p.name} loading="lazy" className="aspect-square w-full rounded-lg object-cover" />
              <div className="mt-4 text-lg font-bold text-navy">{p.name}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-navy/60">{p.role}</div>
              <p className="mt-3 text-sm text-navy/70">{p.bio}</p>
            </Reveal>
          ))}
        </div>
      </Shell>

      <Shell tone="fog" testId="department-heads">
        <H2>Department heads.</H2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {team.departmentHeads.map((p) => (
            <div key={p.name} className="rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-6">
              {p.photo ? <img src={p.photo} alt={p.name} loading="lazy" className="aspect-square w-full rounded-lg object-cover" /> : <div className="aspect-square w-full rounded-lg bg-mist" />}
              <div className="mt-4 text-lg font-bold text-navy">{p.name}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-navy/60">{p.role}</div>
            </div>
          ))}
        </div>
      </Shell>

      <Shell testId="about-how">
        <H2>How we work.</H2>
        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {HOW.map((h) => (
            <div key={h.title} className="border-t-4 border-pink pt-5">
              <h3 className="text-lg font-bold text-navy">{h.title}</h3>
              <p className="mt-3 text-base leading-relaxed text-navy/80">{h.body}</p>
            </div>
          ))}
        </div>
      </Shell>

      <section data-testid="careers-band" className="bg-navy">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-5 py-16 md:flex-row md:items-center md:justify-between">
          <H2 light>We're hiring.</H2>
          <PrimaryButton href={stats.links.careers} testId="careers-btn">See open roles</PrimaryButton>
        </div>
      </section>

      <CTABand title="See if we're a fit." sub="Work with our Agency to get a proven approach tailored to your needs and executed by our results-driven team." />
    </main>
  );
}
